import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import {
  Layers,
  ArrowLeft,
  UploadCloud,
  FileText,
  Loader2,
  Trash2,
  ArrowUp,
  ArrowDown,
  Download,
  X,
  CheckCircle2,
} from "lucide-react";
import {
  useAdminMagazine,
  useRegisterPagesMutation,
  useReorderPagesMutation,
  useDeletePageMutation,
  useAdminMagazineLeads,
  useAdminMagazineRequests,
  useResolveAccessRequestMutation,
} from "./useMagazineQueries";
import { adminMagazineAPI } from "./magazine.api";
import { toast } from "react-toastify";
import * as pdfjsLib from "pdfjs-dist";

// PDF.js worker setup
try {
  if (typeof window !== "undefined") {
    const v = pdfjsLib.version || "3.11.174";
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${v}/pdf.worker.min.js`;
  }
} catch (e) {
  console.warn("Could not set PDF workerSrc:", e);
}

const resolveImageUrl = (urlOrKey) => {
  if (!urlOrKey) return "";
  if (
    urlOrKey.startsWith("http://") ||
    urlOrKey.startsWith("https://") ||
    urlOrKey.startsWith("blob:") ||
    urlOrKey.startsWith("data:")
  ) {
    return urlOrKey;
  }
  const cleanKey = urlOrKey.replace(/^\/+/, "");
  return `https://d23ksuhb21eg5b.cloudfront.net/${cleanKey}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1: PAGE MANAGER (DIRECT S3 UPLOAD + PDF EXTRACTION)
// ─────────────────────────────────────────────────────────────────────────────
function PagesTab({ magazine }) {
  const queryClient = useQueryClient();
  const [pages, setPages] = useState(magazine?.pages || []);
  const [uploading, setUploading] = useState(false);
  const [progressText, setProgressText] = useState("");

  const registerPagesMutation = useRegisterPagesMutation();
  const reorderPagesMutation = useReorderPagesMutation();
  const deletePageMutation = useDeletePageMutation();

  useEffect(() => {
    if (magazine?.pages) {
      setPages(magazine.pages);
    }
  }, [magazine?.pages]);

  const handleImageFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedPayloads = [];
      let startNum =
        pages.length > 0
          ? Math.max(...pages.map((p) => p.page_number || 0)) + 1
          : 1;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgressText(`Uploading page ${i + 1} of ${files.length}...`);

        const ext = file.name.split(".").pop() || "webp";
        const filename = `page_${startNum + i}.${ext}`;

        const { presigned_url, object_key } =
          await adminMagazineAPI.getPresignedUploadUrl({
            filename,
            content_type: file.type || "image/webp",
            magazine_id: Number(magazine.id),
          });

        await adminMagazineAPI.uploadToS3(
          presigned_url,
          file,
          file.type || "image/webp"
        );

        uploadedPayloads.push({
          page_number: startNum + i,
          asset_key: object_key,
          storage_type: "S3",
        });
      }

      setProgressText("Registering pages with database...");
      const res = await registerPagesMutation.mutateAsync({
        id: Number(magazine.id),
        pages: uploadedPayloads,
      });

      setPages((prev) => [...prev, ...res]);
      toast.success(`${files.length} pages uploaded successfully!`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      setProgressText("");
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setProgressText("Uploading PDF to server for high-speed conversion...");

    try {
      // 1. Try Backend Dynamic PDF Import first (pypdfium2, WebP, Thumbnails, S3)
      try {
        const formData = new FormData();
        formData.append("pdf_file", file);
        formData.append("title", magazine?.title || file.name.replace(/\.[^/.]+$/, ""));
        formData.append("slug", magazine?.slug);
        if (magazine?.description) formData.append("description", magazine.description);
        formData.append("access_type", magazine?.access_type || "FREE");
        if (magazine?.allowed_groups) formData.append("allowed_groups", magazine.allowed_groups);
        formData.append("preview_pages_count", magazine?.preview_pages_count || 0);
        formData.append("pricing_type", magazine?.pricing_type || "FREE");
        formData.append("price", magazine?.price || 0);
        formData.append("currency", magazine?.currency || "INR");
        if (magazine?.access_passcode) formData.append("access_passcode", magazine.access_passcode);
        formData.append("allow_screenshot", magazine?.allow_screenshot ? "true" : "false");
        formData.append("pdf_download_enabled", magazine?.pdf_download_enabled ? "true" : "false");
        formData.append("require_email_otp", magazine?.require_email_otp ? "true" : "false");
        formData.append("require_admin_approval", magazine?.require_admin_approval ? "true" : "false");
        formData.append("quality", "98");
        formData.append("scale", "4.5");
        formData.append("overwrite", "true");

        const updatedMag = await adminMagazineAPI.importPdf(formData, (percent) => {
          setProgressText(`Uploading PDF to server (${percent}%)...`);
        });

        if (updatedMag?.pages) {
          setPages(updatedMag.pages);
        }
        queryClient.invalidateQueries(["admin", "magazine", String(magazine.id)]);
        toast.success(
          `PDF imported successfully! ${updatedMag.page_count || updatedMag.pages?.length || ""} pages processed.`
        );
        return;
      } catch (backendErr) {
        // If backend route is not available (404/405), fallback to client-side extractor
        if (backendErr.response?.status !== 404 && backendErr.response?.status !== 405) {
          throw backendErr;
        }
        console.warn("Backend /import-pdf not ready, falling back to client-side extractor...", backendErr);
      }

      // 2. Client-side PDF.js extractor fallback
      setProgressText("Reading PDF file...");
      const arrayBuffer = await file.arrayBuffer();

      // Ensure workerSrc is set to compatible min.js
      const v = pdfjsLib.version || "3.11.174";
      if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${v}/pdf.worker.min.js`;
      }

      const loadingTask = pdfjsLib.getDocument({
        data: new Uint8Array(arrayBuffer),
        cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${v}/cmaps/`,
        cMapPacked: true,
      });

      const pdf = await loadingTask.promise;
      const numPages = pdf.numPages;

      if (!numPages || numPages === 0) {
        throw new Error("The selected PDF file contains no pages.");
      }

      const uploadedPayloads = [];
      let startNum =
        pages.length > 0
          ? Math.max(...pages.map((p) => p.page_number || 0)) + 1
          : 1;

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        setProgressText(
          `Extracting & uploading page ${pageNum} of ${numPages}...`
        );
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 3.0 });

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d", { alpha: false });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Fill white background before rendering PDF page
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, canvas.width, canvas.height);

        await page.render({ canvasContext: context, viewport }).promise;

        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (b) => {
              if (b) resolve(b);
              else {
                canvas.toBlob(
                  (b2) => {
                    if (b2) resolve(b2);
                    else
                      reject(
                        new Error(
                          `Failed to convert page ${pageNum} to image blob.`
                        )
                      );
                  },
                  "image/jpeg",
                  0.9
                );
              }
            },
            "image/webp",
            0.96
          );
        });

        const contentType = blob.type || "image/webp";
        const ext = contentType.includes("png")
          ? "png"
          : contentType.includes("jpeg")
          ? "jpg"
          : "webp";
        const filename = `page_${startNum + pageNum - 1}.${ext}`;

        const { presigned_url, object_key } =
          await adminMagazineAPI.getPresignedUploadUrl({
            filename,
            content_type: contentType,
            magazine_id: Number(magazine.id),
          });

        await adminMagazineAPI.uploadToS3(presigned_url, blob, contentType);

        uploadedPayloads.push({
          page_number: startNum + pageNum - 1,
          asset_key: object_key,
          storage_type: "S3",
        });
      }

      setProgressText("Finalizing edition pages...");
      const res = await registerPagesMutation.mutateAsync({
        id: Number(magazine.id),
        pages: uploadedPayloads,
      });

      setPages((prev) => [...prev, ...res]);
      toast.success(`Extracted & uploaded ${numPages} pages from PDF!`);
    } catch (err) {
      console.error("PDF extraction error:", err);
      const errorMsg =
        err?.message ||
        err?.response?.data?.detail ||
        "Failed to extract pages from PDF.";
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      setProgressText("");
      if (e.target) e.target.value = "";
    }
  };

  const movePage = async (index, direction) => {
    const newPages = [...pages];
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= newPages.length) return;

    const temp = newPages[index];
    newPages[index] = newPages[targetIdx];
    newPages[targetIdx] = temp;

    const pageOrders = newPages.map((p, idx) => ({
      id: p.id,
      page_number: idx + 1,
    }));

    setPages(newPages);
    try {
      await reorderPagesMutation.mutateAsync({
        id: Number(magazine.id),
        pageOrders,
      });
      toast.success("Page order saved.");
    } catch (err) {
      toast.error("Failed to save reordering.");
    }
  };

  const handleDeletePage = async (pageId, num) => {
    if (!window.confirm(`Delete Page ${num}?`)) return;
    try {
      await deletePageMutation.mutateAsync({
        magazineId: Number(magazine.id),
        pageId,
      });
      setPages((prev) => prev.filter((p) => p.id !== pageId));
      toast.success(`Page ${num} deleted.`);
    } catch (err) {
      toast.error("Failed to delete page.");
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col min-h-[60vh]">
      <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <label className="px-3.5 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold text-xs cursor-pointer flex items-center gap-1.5 shadow-2xs transition-all">
            <UploadCloud className="w-4 h-4" />
            <span>Bulk Image Upload</span>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageFiles}
              className="hidden"
              disabled={uploading}
            />
          </label>
          <label className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-semibold text-xs cursor-pointer flex items-center gap-1.5 shadow-2xs transition-all">
            <FileText className="w-4 h-4" />
            <span>Extract from PDF</span>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={handlePdfUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
        </div>
        {uploading && (
          <div className="flex items-center gap-2 text-xs font-semibold text-orange-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{progressText || "Processing upload..."}</span>
          </div>
        )}
      </div>

      <div className="p-6 overflow-y-auto flex-1 bg-white">
        {pages.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-3 text-slate-300">
              <Layers className="w-8 h-8" />
            </div>
            <p className="font-bold text-sm text-slate-800 mb-1">
              No pages added yet
            </p>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Upload individual page images or select a PDF to extract pages automatically.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
            {pages.map((p, idx) => (
              <div
                key={p.id || idx}
                className="group relative flex flex-col bg-white rounded-xl p-2 border border-slate-200 hover:border-orange-500 shadow-2xs hover:shadow-sm transition-all"
              >
                <div className="relative aspect-[453/640] rounded-lg overflow-hidden bg-slate-100 mb-2 border border-slate-200 flex items-center justify-center">
                  {p.asset_url || p.asset_key ? (
                    <img
                      src={resolveImageUrl(p.asset_url || p.asset_key)}
                      alt={`Page ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="text-[10px] text-slate-400 font-mono">
                      Page {idx + 1}
                    </div>
                  )}
                  <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-slate-900/80 text-[9px] font-mono text-white font-bold">
                    p.{idx + 1}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-1 pt-1 border-t border-slate-100">
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={() => movePage(idx, -1)}
                      disabled={idx === 0}
                      className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-20 cursor-pointer"
                      title="Move Left"
                    >
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => movePage(idx, 1)}
                      disabled={idx === pages.length - 1}
                      className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-20 cursor-pointer"
                      title="Move Right"
                    >
                      <ArrowDown className="w-3 h-3" />
                    </button>
                  </div>
                  <button
                    onClick={() => handleDeletePage(p.id, idx + 1)}
                    className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 cursor-pointer"
                    title="Delete Page"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2: LEADS VIEWER & CSV EXPORT
// ─────────────────────────────────────────────────────────────────────────────
function LeadsTab({ magazine }) {
  const { data, isLoading } = useAdminMagazineLeads(Number(magazine.id));
  const [exporting, setExporting] = useState(false);
  const leads = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
    ? data
    : [];

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const blob = await adminMagazineAPI.exportLeadsCsv(Number(magazine.id));
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `leads_${magazine.slug || magazine.id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads CSV exported!");
    } catch (err) {
      toast.error("Failed to export leads.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col min-h-[50vh]">
      <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between rounded-t-2xl">
        <div>
          <p className="text-xs text-slate-600 font-medium">
            {leads.length} reader leads recorded
          </p>
        </div>
        <button
          onClick={handleExportCsv}
          disabled={exporting || leads.length === 0}
          className="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs flex items-center gap-1.5 shadow-2xs disabled:opacity-50 cursor-pointer"
        >
          <Download className="w-3.5 h-3.5" />
          <span>{exporting ? "Exporting..." : "Export CSV"}</span>
        </button>
      </div>
      <div className="p-4 overflow-y-auto flex-1 bg-white">
        {isLoading ? (
          <div className="py-12 flex justify-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : leads.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-xs">
            No leads captured yet for this edition.
          </div>
        ) : (
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-slate-200 text-slate-600 font-semibold bg-slate-50/70">
                <th className="py-2.5 px-3">Name</th>
                <th className="py-2.5 px-3">Email</th>
                <th className="py-2.5 px-3">Phone</th>
                <th className="py-2.5 px-3">OTP Status</th>
                <th className="py-2.5 px-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {leads.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50/60 transition-colors">
                  <td className="py-2.5 px-3 font-medium text-slate-900">{l.name || "—"}</td>
                  <td className="py-2.5 px-3 font-mono text-slate-600">{l.email}</td>
                  <td className="py-2.5 px-3 text-slate-600">{l.phone || "—"}</td>
                  <td className="py-2.5 px-3">
                    {l.is_verified ? (
                      <span className="text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded text-[10px] font-semibold border border-emerald-200">
                        Verified
                      </span>
                    ) : (
                      <span className="text-slate-500 bg-slate-100 px-2 py-0.5 rounded text-[10px]">
                        Direct
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-slate-500">
                    {l.created_at
                      ? new Date(l.created_at).toLocaleDateString()
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3: ACCESS REQUESTS
// ─────────────────────────────────────────────────────────────────────────────
function RequestsTab({
  magazine,
  requests = [],
  pendingRequests = [],
  historyRequests = [],
  isLoading = false,
}) {
  const resolveMutation = useResolveAccessRequestMutation();
  const [resolvingState, setResolvingState] = useState(null);

  const handleResolve = async (reqId, approved) => {
    const action = approved ? "APPROVE" : "REJECT";
    setResolvingState({ id: reqId, action });
    try {
      await resolveMutation.mutateAsync({
        magazineId: Number(magazine.id),
        requestId: reqId,
        payload: {
          action,
          expires_days: 30,
        },
      });
      toast.success(
        `Request ${approved ? "approved" : "rejected"} successfully.`
      );
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to process request.";
      toast.error(msg);
    } finally {
      setResolvingState(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 min-h-[50vh]">
      {isLoading ? (
        <div className="py-12 flex justify-center text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </div>
      ) : requests.length === 0 ? (
        <div className="py-12 text-center text-slate-400 text-xs">
          No access requests submitted yet.
        </div>
      ) : (
        <div className="space-y-6 text-xs">
          {pendingRequests.length > 0 && (
            <div>
              <h3 className="font-bold text-slate-900 mb-3 border-b border-slate-200 pb-2">
                Pending Approvals ({pendingRequests.length})
              </h3>
              <div className="grid gap-3">
                {pendingRequests.map((r) => (
                  <div
                    key={r.id}
                    className="bg-slate-50 rounded-xl p-4 border border-slate-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4"
                  >
                    <div>
                      <div className="font-bold text-sm text-slate-900 mb-0.5">
                        {r.name}
                      </div>
                      <div className="font-mono text-slate-500 mb-1">
                        {r.email}
                      </div>
                      <div className="text-slate-700">
                        <span className="font-semibold text-slate-500">
                          Org:
                        </span>{" "}
                        {r.organization || "—"}
                      </div>
                      <div className="text-slate-700 mt-0.5">
                        <span className="font-semibold text-slate-500">
                          Purpose:
                        </span>{" "}
                        {r.purpose || "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleResolve(r.id, true)}
                        disabled={resolvingState !== null}
                        className="px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold flex items-center justify-center gap-1.5 transition-all min-w-[96px] cursor-pointer shadow-2xs"
                      >
                        {resolvingState?.id === r.id &&
                        resolvingState?.action === "APPROVE" ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Approving...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>Approve</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleResolve(r.id, false)}
                        disabled={resolvingState !== null}
                        className="px-3.5 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-red-50 text-slate-700 hover:text-red-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed font-bold flex items-center justify-center gap-1.5 transition-all min-w-[96px] cursor-pointer shadow-2xs"
                      >
                        {resolvingState?.id === r.id &&
                        resolvingState?.action === "REJECT" ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Rejecting...</span>
                          </>
                        ) : (
                          <>
                            <X className="w-3.5 h-3.5" />
                            <span>Reject</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {historyRequests.length > 0 && (
            <div className="pt-4">
              <h3 className="font-bold text-slate-900 mb-3 border-b border-slate-200 pb-2">
                History
              </h3>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600 font-semibold bg-slate-50/70">
                    <th className="py-2.5 px-3">Name / Email</th>
                    <th className="py-2.5 px-3">Status</th>
                    <th className="py-2.5 px-3">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {historyRequests.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2.5 px-3">
                        <div className="font-medium text-slate-800">
                          {r.name}
                        </div>
                        <div className="font-mono text-slate-400">
                          {r.email}
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        {r.status === "APPROVED" ? (
                          <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-[10px] font-semibold">
                            Approved
                          </span>
                        ) : (
                          <span className="text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded text-[10px] font-semibold">
                            Rejected
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-slate-500">
                        {r.created_at
                          ? new Date(r.created_at).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD WRAPPER
// ─────────────────────────────────────────────────────────────────────────────
export default function AdminMagazineDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const activeTab = ["pages", "leads", "requests"].includes(rawTab)
    ? rawTab
    : "pages";

  const { data: result, isLoading, error } = useAdminMagazine(Number(id));
  const magazine = result?.data || result;

  const { data: requestsResult, isLoading: isRequestsLoading } =
    useAdminMagazineRequests(Number(id));
  const requests = Array.isArray(requestsResult?.data)
    ? requestsResult.data
    : Array.isArray(requestsResult?.items)
    ? requestsResult.items
    : Array.isArray(requestsResult)
    ? requestsResult
    : [];
  const pendingRequests = requests.filter((r) => r.status === "PENDING");
  const historyRequests = requests.filter((r) => r.status !== "PENDING");

  const setTab = (tab) => {
    setSearchParams({ tab });
  };

  if (isLoading) {
    return (
      <div className="p-16 flex flex-col items-center justify-center text-slate-400 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        <span className="text-xs font-medium text-slate-600">Loading edition...</span>
      </div>
    );
  }

  if (error || !magazine) {
    return (
      <div className="p-16 flex flex-col items-center justify-center text-slate-400 gap-3">
        <span className="text-xs text-slate-600">Edition not found.</span>
        <button
          onClick={() => navigate("/admin/magazines")}
          className="text-orange-600 font-bold hover:underline cursor-pointer"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto bg-slate-50/50 min-h-screen">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/admin/magazines")}
          className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-100 text-slate-600 transition-colors cursor-pointer shadow-2xs"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Manage Edition: {magazine.title}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Manage pages, leads, and access requests for this publication.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200 mb-6 bg-white p-2 rounded-2xl shadow-2xs">
        <button
          onClick={() => setTab("pages")}
          className={`px-4 py-2 font-bold text-xs rounded-xl transition-all cursor-pointer ${
            activeTab === "pages"
              ? "bg-orange-600 text-white shadow-xs"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          Pages ({magazine.page_count ?? (magazine.pages?.length || 0)})
        </button>
        <button
          onClick={() => setTab("leads")}
          className={`px-4 py-2 font-bold text-xs rounded-xl transition-all cursor-pointer ${
            activeTab === "leads"
              ? "bg-orange-600 text-white shadow-xs"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          Leads ({magazine.lead_count ?? 0})
        </button>
        <button
          onClick={() => setTab("requests")}
          className={`inline-flex items-center gap-1.5 px-4 py-2 font-bold text-xs rounded-xl transition-all cursor-pointer ${
            activeTab === "requests"
              ? "bg-orange-600 text-white shadow-xs"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          <span>Access Requests</span>
          {pendingRequests.length > 0 ? (
            <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-white leading-none">
              {pendingRequests.length}
            </span>
          ) : requests.length > 0 ? (
            <span className="text-xs font-normal text-slate-400">
              ({requests.length})
            </span>
          ) : null}
        </button>
      </div>

      <div>
        {activeTab === "pages" && <PagesTab magazine={magazine} />}
        {activeTab === "leads" && <LeadsTab magazine={magazine} />}
        {activeTab === "requests" && (
          <RequestsTab
            magazine={magazine}
            requests={requests}
            pendingRequests={pendingRequests}
            historyRequests={historyRequests}
            isLoading={isRequestsLoading}
          />
        )}
      </div>
    </div>
  );
}
